import { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { useNavigate } from 'react-router-dom';
import { getUserRestaurant } from '../utils/restaurantUtils';
import AdminNavigation from '../components/AdminNavigation';
import { Save, Globe } from 'lucide-react';

function RestaurantSettings() {
  const navigate = useNavigate();
  const [restaurantId, setRestaurantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customerAppUrl, setCustomerAppUrl] = useState('');
  const [restaurantName, setRestaurantName] = useState('');

  useEffect(() => {
    const fetchRestaurant = async () => {
      if (!auth.currentUser) {
        navigate('/login');
        return;
      }
      const resId = await getUserRestaurant(auth.currentUser.uid);
      setRestaurantId(resId || 'demo-restaurant');
      
      // Fetch restaurant data
      const restaurantDoc = await getDoc(doc(db, 'restaurants', resId || 'demo-restaurant'));
      const data = restaurantDoc.data();
      setRestaurantName(data?.name || '');
      setCustomerAppUrl(data?.customerAppUrl || import.meta.env.VITE_CUSTOMER_APP_URL || '');
      setLoading(false);
    };
    fetchRestaurant();
  }, [navigate]);

  const handleSave = async () => {
    if (!restaurantId) return;
    
    setSaving(true);
    try {
      await updateDoc(doc(db, 'restaurants', restaurantId), {
        customerAppUrl: customerAppUrl.trim()
      });
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavigation />

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
        <div className="mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">Restaurant Settings</h2>
          <p className="text-sm text-gray-600 mt-1">{restaurantName}</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
          <div className="space-y-6">
            {/* Customer App URL */}
            <div>
              <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700 mb-2">
                <Globe className="w-5 h-5 text-primary-600" />
                <span>Customer App Domain</span>
              </label>
              <input
                type="url"
                value={customerAppUrl}
                onChange={(e) => setCustomerAppUrl(e.target.value)}
                placeholder="https://order.yourrestaurant.com"
                className="input-field"
              />
              <p className="mt-2 text-sm text-gray-600">
                This is the domain where customers access the menu. QR codes will point to this URL.
                <br />
                <strong>Current:</strong> {customerAppUrl || 'Not set (using default)'}
              </p>
              <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>💡 Tip:</strong> When you add a custom domain in Vercel for your customer app, 
                  update this field and save. All new QR codes will automatically use your custom domain!
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary flex items-center space-x-2"
              >
                <Save className="w-5 h-5" />
                <span>{saving ? 'Saving...' : 'Save Settings'}</span>
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default RestaurantSettings;
